import { definedMap } from "@glideapps/ts-necessities";

import { TableName, builderActionsCollectionName, isSample } from "../database-strings";
import { logError } from "../debug-print";
import { CompoundActionNode, validateCompoundActionNode } from "../generator/actions/compound";
import { assert, checkArray, checkString, isArray, mapFilterUndefined } from "../Support";
import { DataReader, Database, DocumentData, Query, unzipString, zipString } from ".";

export interface BuilderActionPerApp {
    readonly tableName: TableName;
}

export interface BuilderAction {
    // zipped JSON
    readonly action: CompoundActionNode;

    readonly title?: string;
    readonly description?: string;

    readonly appIDs: readonly string[];

    // indexed by app ID
    readonly perApp: Partial<Record<string, BuilderActionPerApp>>;
}

export type BuilderActionsForApp = ReadonlyMap<string, BuilderAction>;

export function makeBuilderActionFromDocument(data: DocumentData): BuilderAction | undefined {
    // We currently have a race condition or something of the sort that leads
    // to action documents that only have the `editors` array set:
    // https://console.firebase.google.com/u/0/project/glide-prod/firestore/data~2Fbuilder-actions~2F5YLyo3wqy1R8zz5Rzqwt
    if (!isArray(data.appIDs)) return undefined;
    return {
        appIDs: checkArray<string, unknown>(data.appIDs, checkString),
        action: JSON.parse(unzipString(data.action)),
        title: definedMap(data.title, checkString),
        description: definedMap(data.description, checkString),
        perApp: data.perApp,
    };
}

export async function loadActionsForApp(
    db: Database,
    appID: string,
    authUserID: string | undefined
): Promise<Map<string, BuilderAction>> {
    try {
        const queries: Query[] = [];
        if (isSample(appID)) {
            // FIXME: This is super shitty.  For some reason we can't check in
            // the rule whether the app is a sample by looking into the `appIDs`.
            queries.push({
                fieldPath: "isSample",
                opString: "==",
                value: true,
            });
        } else if (authUserID !== undefined) {
            // FIXME: This is shitty.  We can't do two `array-contains` in one
            // query, so we query for all actions of which the user is editor,
            // and then we filter manually.
            queries.push({
                fieldPath: "editors",
                opString: "array-contains",
                value: authUserID,
            });
        } else {
            queries.push({
                fieldPath: "appIDs",
                opString: "array-contains",
                value: appID,
            });
        }
        const docs = await db.getDocumentsWhere(builderActionsCollectionName, queries);

        const builderActions: [string, BuilderAction][] = mapFilterUndefined(docs, ({ id, data }) => {
            const action = makeBuilderActionFromDocument(data);
            if (action === undefined) return undefined;
            if (action.appIDs.indexOf(appID) < 0) return undefined;
            return [id, action];
        });

        // FIXME: This is even uglier.  If we're support or admin, we can't
        // query the actions by editor, but we need to query by app ID.
        // Unfortunately we don't usually know whether we're the owner of the
        // app when we load the actions, so if we don't find any actions
        // when querying by editor, we query by app ID just in case.
        if (builderActions.length === 0 && authUserID !== undefined && !isSample(appID)) {
            return await loadActionsForApp(db, appID, undefined);
        }

        return new Map(builderActions);
    } catch (e: unknown) {
        logError("Error loading actions", appID, e);
        return new Map();
    }
}

export async function loadBuilderAction(db: DataReader, actionID: string): Promise<BuilderAction | undefined> {
    const doc = await db.getDocument(builderActionsCollectionName, actionID);
    if (doc === undefined) return undefined;

    return makeBuilderActionFromDocument(doc);
}

export async function saveBuilderAction(
    db: Database,
    appID: string,
    actionID: string | undefined,
    tableName: TableName,
    title: string | undefined,
    description: string | undefined,
    action: CompoundActionNode
): Promise<string> {
    assert(validateCompoundActionNode(action) === undefined);

    const doc: DocumentData = {
        appIDs: [appID],
        action: zipString(JSON.stringify(action)),
        title: title ?? db.deleteFieldValue,
        description: description ?? db.deleteFieldValue,
        perApp: {
            [appID]: {
                tableName,
            },
        },
        lastModifiedAt: db.serverTimestampFieldValue,
    };
    if (actionID === undefined) {
        doc.createdAt = db.serverTimestampFieldValue;
    }
    actionID = await db.updateDocument(builderActionsCollectionName, actionID, doc);
    return actionID;
}
